import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  from,
  firstValueFrom,
  Subscription,
  Subject,
  Observable,
  ObservableInput,
  defer,
  forkJoin
} from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { AIFireService, VideoJobUpdate, AnswerLength } from './fire.service';
import { ImageRef, Message, Conversation } from '../models/chat.model';
import { ref, set, get, child, remove, onValue, DatabaseReference } from 'firebase/database';
import {
  addDoc,
  collection,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { database, db, firebaseApp } from './fire-init';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';

@Injectable({ providedIn: 'root' })
export class ChatFireService {
  /**
   * History scope to keep text/image/video chats isolated.
   * - `text` keeps legacy path (`conversations/<uid>`) for backward compatibility.
   * - `image` and `video` use dedicated roots.
   */
  private readonly historyNamespaceSubject = new BehaviorSubject<'text' | 'image' | 'video'>('text');
  historyNamespace$ = this.historyNamespaceSubject.asObservable();

  private readonly MODEL_LABELS: Record<string, string> = {
    nova2: 'Nova 2',
    chatgpt54: 'ChatGPT 5.4',
    minichatgpt54: 'ChatGPT Mini',
    codestralto: 'Codestral',
    maverick: 'Llama 4',
    opus46: 'Claude Opus 4.6',
    opus47: 'Claude Opus 4.7',
    opus37: 'Claude Opus 3.7',
    opus6: 'Claude Opus 4.6',
    kimik25: 'Kimi 2.5',
    qwen: 'Qwen 3 Coder',
    glm5: 'GLM 5',
    haiku45: 'Claude Haiku 4.5',
    sonnet45: 'Sonnet 4.5',
    claude4: 'Claude 4 (Sonnet)',
    opuslegacy: 'Opus (legacy)',
    stability: 'Stable Diffusion 3.5',
    stability35l: 'Stability SD 3.5 Large',
    palmx: 'Writer X5',
    dale: 'DALL·E 3',
    cohere: 'Cohere R+',
    commandLight: 'Command Instruct',
    gemini: 'Gemini Pro',
    gemini3flash: 'Gemini Flash 3',
    deepseek: 'DeepSeek R1',
    deepv3: 'DeepSeek V3.2',
    mistrallarge: 'Mistral Large',
    pixtralimage: 'Pixtral',
    pegasus: 'Pegasus 1.2',
    cogvideox3: 'CogVideoX-3',
    novareel11: 'Nova Reel 1.1',
    veo: 'Veo 3.1',
    dale2legacy: 'DALL·E 2',
    gpt4o_audio_text: 'GPT-4o Audio',
    gpt4o_audio_realtime: 'GPT-4o Realtime',
    scout: 'Meta Scout',
    jamba: 'Jamba Large'
  };
  private labelFor = (id: string) => this.MODEL_LABELS[id] ?? id;

  private static readonly ANON_UID_STORAGE_KEY = 'clarichat_anon_uid';
  private static readonly SESSION_ID_STORAGE_KEY = 'clarichat_session_id';
  private static readonly SESSION_LAST_ACTIVE_KEY = 'clarichat_session_last_active_at';
  private static readonly SESSION_TTL_MS = 30 * 60 * 1000;

  private currentUid: string | null = null;
  private currentSessionId: string | null = null;
  private suppressAnonRecreation = false;
  private authUserIsAnonymous = false;

  private uidSubject = new BehaviorSubject<string | null>(null);
  uid$ = this.uidSubject.asObservable();
  private authStateSubject = new BehaviorSubject<boolean>(false);
  authState$ = this.authStateSubject.asObservable();
  isAuthenticated$ = this.authState$;

  private sessionIdSubject = new BehaviorSubject<string | null>(null);
  sessionId$ = this.sessionIdSubject.asObservable();

  private conversationsSubject = new BehaviorSubject<Conversation[]>([]);
  private currentConversationSubject = new BehaviorSubject<Conversation | null>(null);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);

  private allConversationsByUidSubject = new BehaviorSubject<Conversation[]>([]);
  private allConversationsListenerUnsubscribe: (() => void) | null = null;
  private allConversationsRef: DatabaseReference | null = null;

  conversations$ = this.conversationsSubject.asObservable();
  currentConversation$ = this.currentConversationSubject.asObservable();
  isLoading$ = this.isLoadingSubject.asObservable();
  allConversationsByUid$ = this.allConversationsByUidSubject.asObservable();

  private rtSession: {
    pc: RTCPeerConnection;
    dc: RTCDataChannel;
    localStream: MediaStream | null;
    remoteAudioEl: HTMLAudioElement;
    sendText: (text: string) => void;
    muteMic: (muted: boolean) => void;
    startUserTurn?: () => void;
    endUserTurn?: () => void;
    close: () => void;
  } | null = null;
  private rtEventsSub: Subscription | null = null;

  private realtimeActiveSubject = new BehaviorSubject<boolean>(false);
  realtimeActive$ = this.realtimeActiveSubject.asObservable();

  private remoteAudioElSubject = new BehaviorSubject<HTMLAudioElement | null>(null);
  remoteAudioEl$ = this.remoteAudioElSubject.asObservable();

  private conversationsListenerUnsubscribe: (() => void) | null = null;
  private conversationsRef: DatabaseReference | null = null;
  private messagesListenerUnsubscribe: (() => void) | null = null;
  private messagesListenerConversationId: string | null = null;

  constructor(private aiService: AIFireService) {
    this.initUserContext();
  }

  logout(): Observable<void> {
    return this.toObservable(async () => {
      this.suppressAnonRecreation = true;
      this.authStateSubject.next(false);
      await firstValueFrom(this.clearClientSessionStateForLogout());

      const auth = getAuth(firebaseApp);
      try {
        await signOut(auth);
      } catch (error) {
        console.warn('[ChatFireService] signOut failed during logout', error);
      }
      try {
        const fallbackAuth = getAuth();
        if (fallbackAuth !== auth) {
          await signOut(fallbackAuth);
        }
      } catch {}

      // Ensure local auth/UI state stays logged out even if upstream auth callbacks lag.
      await firstValueFrom(this.setCurrentUid(null));
      await firstValueFrom(this.setCurrentSessionId(null));
      this.authStateSubject.next(false);
    });
  }

  private isAuthenticatedUid(uid: string | null): boolean {
    return !!uid && !uid.startsWith('anon-') && !this.authUserIsAnonymous;
  }

  /**
   * Emits once after Firebase has reported the initial auth user (or absence).
   * True only for a signed-in, non-anonymous user (same bar as {@link authState$} when true).
   */
  isRealUserResolved$(): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      try {
        const auth = getAuth(firebaseApp);
        const unsub = onAuthStateChanged(auth, (user) => {
          unsub();
          subscriber.next(!!user && !user.isAnonymous);
          subscriber.complete();
        });
        return () => unsub();
      } catch {
        subscriber.next(false);
        subscriber.complete();
      }
    });
  }

  private toObservable<T>(fn: () => ObservableInput<T>): Observable<T> {
    return defer(() => from(fn()));
  }

  private normalizeRequestedModelId(modelId?: string | null): string {
    const normalized = String(modelId || '').trim().toLowerCase();

    switch (normalized) {
      case 'chatgpt52':
      case 'chatgpt5':
      case 'chagpt54':
        return 'chatgpt54';
      case 'opus6':
        return 'opus46';
      case 'opus7':
        return 'opus47';
      case 'opus45':
        return 'opus46';
      case 'opus37':
      case 'opus3.7':
      case 'claudeopus37':
      case 'claude-opus-3.7':
        return 'opus37';
      case 'o3':
        return 'chatgpt54';
      case 'haiku':
      case 'haiku35':
      case 'haiku3':
      case 'haiku3.5':
      case 'haiku4':
      case 'haiku4.5':
      case 'claudehaiku':
      case 'claudehaiku45':
      case 'claude-haiku-4.5':
        return 'haiku45';
      case 'jambalarge':
      case 'jamba-large':
      case 'jamba_large':
        return 'jamba';
      case 'kimi25':
      case 'kimi-2.5':
      case 'kimi2.5':
      case 'moonshot-kimi':
        return 'kimik25';
      case 'qwen3':
      case 'qwen':
      case 'qwen-coder':
        return 'qwen';
      case 'glm-5':
      case 'glm5':
      case 'glm':
        return 'glm5';
      case 'pegusus':
        return 'pegasus';
      default:
        return normalized || 'palmx';
    }
  }

  private readLastActiveMs(): number {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return 0;
      const raw = window.localStorage.getItem(ChatFireService.SESSION_LAST_ACTIVE_KEY);
      const n = raw ? Number(raw) : 0;
      return Number.isFinite(n) ? n : 0;
    } catch {
      return 0;
    }
  }

  private writeLastActiveNow(): void {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(ChatFireService.SESSION_LAST_ACTIVE_KEY, String(Date.now()));
      }
    } catch {}
  }

  private isSessionExpired(): boolean {
    const last = this.readLastActiveMs();
    if (!last) return false;
    return Date.now() - last > ChatFireService.SESSION_TTL_MS;
  }

  private ensureActiveSession(): void {
    if (!this.currentSessionId) {
      this.startNewSession();
      return;
    }

    if (this.isSessionExpired()) this.startNewSession();
    else this.writeLastActiveNow();
  }

  private initUserContext(): void {
    if (this.isSessionExpired()) {
      this.startNewSession();
    } else {
      const existingSessionId = this.getOrCreateSessionId();
      if (existingSessionId) {
        this.setCurrentSessionId(existingSessionId).subscribe();
      }
    }

    this.writeLastActiveNow();

    try {
      const auth = getAuth(firebaseApp);
      onAuthStateChanged(auth, (user) => {
        this.authUserIsAnonymous = !!user?.isAnonymous;
        if (user?.uid) {
          this.suppressAnonRecreation = false;

          this.setCurrentUid(user.uid).subscribe();
          this.authStateSubject.next(!this.authUserIsAnonymous);
          return;
        }

        if (this.suppressAnonRecreation) {
          this.setCurrentUid(null).subscribe();
          this.setCurrentSessionId(null).subscribe();
          this.authStateSubject.next(false);
          return;
        }

        this.setCurrentUid(this.getOrCreateAnonymousUid()).subscribe();
        this.authStateSubject.next(false);

        if (!this.currentSessionId) {
          this.startNewSession();
        }
      });
    } catch {
      this.setCurrentUid(this.getOrCreateAnonymousUid()).subscribe();
      this.authStateSubject.next(false);
    }
  }

  private getOrCreateAnonymousUid(): string {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return 'anon-' + uuidv4();
      const key = ChatFireService.ANON_UID_STORAGE_KEY;
      let anon = window.localStorage.getItem(key);
      if (!anon) {
        anon = 'anon-' + uuidv4();
        window.localStorage.setItem(key, anon);
      }
      return anon;
    } catch {
      return 'anon-' + uuidv4();
    }
  }

  private getOrCreateSessionId(): string {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return 'session-' + uuidv4();
      const key = ChatFireService.SESSION_ID_STORAGE_KEY;
      let sid = window.localStorage.getItem(key);
      if (!sid) {
        sid = 'session-' + uuidv4();
        window.localStorage.setItem(key, sid);
      }
      return sid;
    } catch {
      return 'session-' + uuidv4();
    }
  }

  public clearClientSessionStateForLogout(): Observable<void> {
    return this.toObservable(async () => {
      this.suppressAnonRecreation = true;

      try { this.rtEventsSub?.unsubscribe(); } catch {}
      this.rtEventsSub = null;

      try { this.rtSession?.close(); } catch {}
      this.rtSession = null;

      this.realtimeActiveSubject.next(false);
      this.remoteAudioElSubject.next(null);

      this.clearMessagesListener();

      if (this.conversationsListenerUnsubscribe) {
        this.conversationsListenerUnsubscribe();
        this.conversationsListenerUnsubscribe = null;
      }
      this.conversationsRef = null;

      if (this.allConversationsListenerUnsubscribe) {
        this.allConversationsListenerUnsubscribe();
        this.allConversationsListenerUnsubscribe = null;
      }
      this.allConversationsRef = null;

      this.currentConversationSubject.next(null);
      this.conversationsSubject.next([]);
      this.allConversationsByUidSubject.next([]);
      this.isLoadingSubject.next(false);

      this.currentSessionId = null;
      this.sessionIdSubject.next(null);

      this.currentUid = null;
      this.uidSubject.next(null);
      this.authUserIsAnonymous = false;

      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.removeItem(ChatFireService.ANON_UID_STORAGE_KEY);
          window.localStorage.removeItem(ChatFireService.SESSION_ID_STORAGE_KEY);
          window.localStorage.removeItem(ChatFireService.SESSION_LAST_ACTIVE_KEY);
          window.localStorage.removeItem('sessionId');
          window.localStorage.removeItem('tempChatActive');
        }

        if (typeof window !== 'undefined' && window.sessionStorage) {
          window.sessionStorage.removeItem('sessionId');
        }
      } catch {}
    });
  }

  public resetLogoutSuppression(): void {
    this.suppressAnonRecreation = false;
  }

  startNewSession(): void {
    this.suppressAnonRecreation = false;

    const sid = 'session-' + uuidv4();
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(ChatFireService.SESSION_ID_STORAGE_KEY, sid);
        window.localStorage.setItem(ChatFireService.SESSION_LAST_ACTIVE_KEY, String(Date.now()));
      }
    } catch {}

    this.setCurrentSessionId(sid).subscribe();

    this.conversationsSubject.next([]);
    this.setCurrentConversation(null);
  }

  private setCurrentSessionId(sessionId: string | null): Observable<void> {
    return this.toObservable(async () => {
      if (this.currentSessionId === sessionId) return;
      this.currentSessionId = sessionId;
      this.sessionIdSubject.next(sessionId);

      if (this.currentUid && this.currentSessionId) {
        this.setupConversationsListener();
        this.setupAllConversationsByUidListener();
      } else {
        this.clearMessagesListener();

        if (this.conversationsListenerUnsubscribe) {
          this.conversationsListenerUnsubscribe();
          this.conversationsListenerUnsubscribe = null;
        }
        this.conversationsRef = null;

        this.conversationsSubject.next([]);
        this.currentConversationSubject.next(null);
      }
    });
  }

  private setCurrentUid(uid: string | null): Observable<void> {
    return this.toObservable(async () => {
      if (this.currentUid === uid) return;
      this.currentUid = uid;
      this.uidSubject.next(uid);
      this.authStateSubject.next(this.isAuthenticatedUid(uid));

      if (uid && this.currentSessionId) {
        this.setupConversationsListener();
        this.setupAllConversationsByUidListener();
      } else {
        if (this.conversationsListenerUnsubscribe) {
          this.conversationsListenerUnsubscribe();
          this.conversationsListenerUnsubscribe = null;
        }
        this.conversationsRef = null;

        if (this.allConversationsListenerUnsubscribe) {
          this.allConversationsListenerUnsubscribe();
          this.allConversationsListenerUnsubscribe = null;
        }
        this.allConversationsRef = null;

        this.conversationsSubject.next([]);
        this.currentConversationSubject.next(null);
        this.allConversationsByUidSubject.next([]);
      }
    });
  }

  private conversationsRootPath(): string | null {
    if (!this.currentUid) return null;
    return this.namespaceRootPath(this.currentUid, this.historyNamespaceSubject.value);
  }

  private namespaceRootPath(uid: string, namespace: 'text' | 'image' | 'video'): string {
    if (namespace === 'text') {
      return `conversations/${uid}`;
    }
    return `conversations_${namespace}/${uid}`;
  }

  setChatHistoryNamespace(namespace: 'text' | 'image' | 'video'): Observable<void> {
    return this.toObservable(async () => {
      const nextNamespace = namespace || 'text';
      if (this.historyNamespaceSubject.value === nextNamespace) {
        return;
      }

      this.historyNamespaceSubject.next(nextNamespace);
      this.clearMessagesListener();
      this.conversationsSubject.next([]);
      this.currentConversationSubject.next(null);
      this.allConversationsByUidSubject.next([]);

      if (this.currentUid && this.currentSessionId) {
        this.setupConversationsListener();
        this.setupAllConversationsByUidListener();
      }
    });
  }

  private ensureUidOrThrow(): string {
    if (!this.currentUid) throw new Error('No UID resolved for ChatFireService.');
    return this.currentUid;
  }

  private ensureSessionIdOrThrow(): string {
    if (!this.currentSessionId) throw new Error('No sessionId resolved for ChatFireService.');
    return this.currentSessionId;
  }

  private removeUndefinedValues(obj: any): any {
    if (obj === null || obj === undefined) return null;
    if (Array.isArray(obj)) return obj.map((item) => this.removeUndefinedValues(item));
    if (typeof obj === 'object') {
      const cleaned: any = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const value = obj[key];
          if (value !== undefined) cleaned[key] = this.removeUndefinedValues(value);
        }
      }
      return cleaned;
    }
    return obj;
  }

  private updateConversationInMemory(c: Conversation): void {
    const existing = this.conversationsSubject.value.filter((v) => v.id !== c.id);
    this.conversationsSubject.next([c, ...existing].slice(0, 10));
    this.setCurrentConversation(c);
  }

  private setCurrentConversation(c: Conversation | null): void {
    this.currentConversationSubject.next(c);
    if (c?.id && this.isRealtimeConversationId(c.id)) this.setupMessagesListener(c.id);
    else this.clearMessagesListener();
  }

  private isRealtimeConversationId(conversationId: string): boolean {
    return !conversationId.startsWith('temp-');
  }

  private clearMessagesListener(): void {
    if (this.messagesListenerUnsubscribe) {
      this.messagesListenerUnsubscribe();
      this.messagesListenerUnsubscribe = null;
    }
    this.messagesListenerConversationId = null;
  }

  private setupMessagesListener(conversationId: string): void {
    if (!this.isRealtimeConversationId(conversationId)) {
      this.clearMessagesListener();
      return;
    }

    if (this.messagesListenerConversationId === conversationId && this.messagesListenerUnsubscribe) {
      return;
    }

    this.clearMessagesListener();
    this.messagesListenerConversationId = conversationId;

    const messagesRef = collection(db, 'conversations', conversationId, 'messages');
    const q = query(messagesRef, orderBy('createdAt', 'asc'));

    this.messagesListenerUnsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const messages = snapshot.docs.map((doc) => {
          const data = doc.data() as any;
          const role = data?.type === 'assistant' ? 'assistant' : data?.type === 'system' ? 'system' : 'user';
          const timestamp = data?.createdAt?.toDate ? data.createdAt.toDate() : new Date();
          return {
            id: doc.id,
            role,
            content: data?.text ?? '',
            timestamp,
            modelId: data?.meta?.model,
            modelDisplayName: data?.meta?.modelDisplayName,
            uid: data?.uid,
            sessionId: data?.sessionId
          } as Message;
        });

        this.applyRealtimeMessages(conversationId, messages);
      },
      (err) => {
        console.error('[ChatFireService] messages listener error', err);
      }
    );
  }

  private applyRealtimeMessages(conversationId: string, messages: Message[]): void {
    const current = this.currentConversationSubject.value;
    if (current?.id === conversationId) {
      this.currentConversationSubject.next({
        ...current,
        messages,
        updatedAt: messages[messages.length - 1]?.timestamp || current.updatedAt
      });
    }

    const updated = this.conversationsSubject.value.map((conv) =>
      conv.id === conversationId
        ? {
            ...conv,
            messages,
            updatedAt: messages[messages.length - 1]?.timestamp || conv.updatedAt
          }
        : conv
    );
    this.conversationsSubject.next(updated);
  }

  private async addFirestoreMessage(conversationId: string, msg: Message): Promise<void> {
    if (!this.isRealtimeConversationId(conversationId)) return;
    const messagesRef = collection(db, 'conversations', conversationId, 'messages');
    const payload: any = {
      type: msg.role,
      text: msg.content,
      createdAt: serverTimestamp(),
      uid: msg.uid,
      sessionId: msg.sessionId
    };
    if (msg.modelId || msg.modelDisplayName) {
      payload.meta = {
        ...(msg.modelId ? { model: msg.modelId } : {}),
        ...(msg.modelDisplayName ? { modelDisplayName: msg.modelDisplayName } : {})
      };
    }
    await addDoc(messagesRef, payload);
  }

  private async safeAddFirestoreMessage(conversationId: string, msg: Message): Promise<void> {
    try {
      await this.addFirestoreMessage(conversationId, msg);
    } catch (err) {
      console.warn('[ChatFireService] Firestore message write failed', err);
    }
  }

  private async requestGeminiReply(conversationId: string, systemPrompt?: string): Promise<void> {
    const functions = getFunctions(firebaseApp, 'us-central1');
    const callable = httpsCallable(functions, 'generateAiReply');
    await callable({ conversationId, systemPrompt });
  }

  private saveConversationToDatabase(c: Conversation): Observable<void> {
    return this.toObservable(async () => {
      const uid = this.ensureUidOrThrow();
      const rootPath = this.namespaceRootPath(uid, this.historyNamespaceSubject.value);
      try {
        const isTempChat = localStorage.getItem('tempChatActive') === 'true';
        let cleanedConversation = this.removeUndefinedValues(c);

        if (isTempChat) {
          const tempId = `temp-${cleanedConversation.id}`;
          cleanedConversation = {
            ...cleanedConversation,
            id: tempId,
            messages:
              cleanedConversation.messages?.map((msg: any) => ({
                ...msg,
                content: msg.content ? `temp-${msg.content}` : msg.content
              })) || []
          };
          await set(ref(database, `${rootPath}/${tempId}`), cleanedConversation);
        } else {
          await set(ref(database, `${rootPath}/${c.id}`), cleanedConversation);
        }
      } catch (err) {
        console.error('[ChatFireService] saveConversationToDatabase error', err);
      }
    });
  }

  updateConversation(c: Conversation): Observable<void> {
    return this.toObservable(async () => {
      this.ensureActiveSession();
      c.updatedAt = new Date();

      if (!c.topic && c.messages && c.messages.length > 0) c.topic = this.extractTopic(c);

      this.updateConversationInMemory(c);
      await firstValueFrom(this.saveConversationToDatabase(c));
    });
  }

  createNewConversation(): Observable<Conversation> {
    return this.toObservable(async () => {
      this.ensureActiveSession();

      const now = new Date();
      const uid = this.ensureUidOrThrow();
      const sessionId = this.ensureSessionIdOrThrow();

      const isTempChat = localStorage.getItem('tempChatActive') === 'true';
      const baseId = uuidv4();
      const conversationId = isTempChat ? `temp-${baseId}` : baseId;
      const tempSessionId = isTempChat && sessionId ? `temp-${sessionId}` : sessionId;

      const c: Conversation = {
        id: conversationId,
        title: isTempChat ? 'Temporary Chat' : 'New Conversation',
        messages: [],
        createdAt: now,
        updatedAt: now,
        uid,
        sessionId: tempSessionId
      };

      this.conversationsSubject.next([c, ...this.conversationsSubject.value].slice(0, 5));
      this.setCurrentConversation(c);
      await firstValueFrom(this.saveConversationToDatabase(c));
      return c;
    });
  }

  selectConversation(id: string): void {
    this.ensureActiveSession();
    const c = this.conversationsSubject.value.find((v) => v.id === id) || null;
    this.setCurrentConversation(c);
  }

  loadConversationById(conversationId: string): Observable<Conversation | null> {
    return this.toObservable(async () => {
      const uid = this.ensureUidOrThrow();
      const rootPath = this.namespaceRootPath(uid, this.historyNamespaceSubject.value);
      try {
        const snapshot = await get(child(ref(database), `${rootPath}/${conversationId}`));
        if (!snapshot.exists()) return null;

        const raw = snapshot.val();
        const c: Conversation = {
          id: raw.id || conversationId,
          title: raw.title || 'Conversation',
          messages: (raw.messages || []).map((m: any) => ({
            ...m,
            timestamp: m.timestamp ? new Date(m.timestamp) : new Date()
          })),
          createdAt: raw.createdAt ? new Date(raw.createdAt) : new Date(),
          updatedAt: raw.updatedAt ? new Date(raw.updatedAt) : new Date(),
          uid: raw.uid,
          sessionId: raw.sessionId,
          topic: raw.topic
        };

        this.setCurrentConversation(c);

        const existing = this.conversationsSubject.value.find((v) => v.id === c.id);
        if (!existing) this.conversationsSubject.next([c, ...this.conversationsSubject.value].slice(0, 10));

        return c;
      } catch (err) {
        console.error('[ChatFireService] loadConversationById error', err);
        return null;
      }
    });
  }

  getRecentConversations(limit: number = 5): Observable<Conversation[]> {
    return this.toObservable(async () => {
      const uid = this.ensureUidOrThrow();
      const rootPath = this.namespaceRootPath(uid, this.historyNamespaceSubject.value);
      try {
        const snapshot = await get(child(ref(database), rootPath));
        const conversations: Conversation[] = [];

        if (snapshot.exists()) {
          const data = snapshot.val();
          const keys = Object.keys(data)
            .map((key) => ({ key, raw: data[key] }))
            .map(({ key, raw }) => ({
              key,
              updatedAt: raw.updatedAt ? new Date(raw.updatedAt) : new Date(0)
            }))
            .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
            .slice(0, limit);

          for (const { key } of keys) {
            const raw = data[key];
            const messages = (raw.messages || []).map((m: any) => ({
              ...m,
              timestamp: m.timestamp ? new Date(m.timestamp) : new Date()
            }));

            const c: Conversation = {
              id: raw.id || key,
              title: raw.title || 'Conversation',
              messages,
              createdAt: raw.createdAt ? new Date(raw.createdAt) : new Date(),
              updatedAt: raw.updatedAt ? new Date(raw.updatedAt) : new Date(),
              uid: raw.uid,
              sessionId: raw.sessionId,
              topic: this.extractLargestWordFromMessages(messages)
            };

            conversations.push(c);
          }
        }

        return conversations;
      } catch (err) {
        console.error('[ChatFireService] getRecentConversations error', err);
        return [];
      }
    });
  }

  private extractTopic(conversation: Conversation): string {
    return this.extractLargestWordFromMessages(conversation.messages || []);
  }

  private extractTopicFromMessages(messages: Message[]): string {
    return this.extractLargestWordFromMessages(messages);
  }

  getConversationTopicFromMessages(messages: Message[]): string {
    return this.extractLargestWordFromMessages(messages);
  }

  private extractLargestWordFromMessages(messages: Message[]): string {
    if (!messages || messages.length === 0) return 'Conversation';

    const allText = messages
      .map((m) => m.content || '')
      .join(' ');

    const words = allText.match(/\b[a-zA-Z0-9']+\b/g) || [];

    if (words.length === 0) return 'Conversation';

    let largestWord = words[0];
    for (let i = 1; i < words.length; i++) {
      if (words[i].length > largestWord.length) {
        largestWord = words[i];
      }
    }
    return largestWord;
  }

  private createUserMsg(content: string): Message {
    this.ensureActiveSession();
    const uid = this.ensureUidOrThrow();
    const sessionId = this.ensureSessionIdOrThrow();
    return { id: uuidv4(), role: 'user', content, timestamp: new Date(), uid, sessionId } as any;
  }

  private createAssistantMsg(modelId: string, text: string, image?: string | null, tokens?: number): Message {
    this.ensureActiveSession();
    const uid = this.ensureUidOrThrow();
    const sessionId = this.ensureSessionIdOrThrow();
    const normalizedModelId = this.normalizeRequestedModelId(modelId);
    return {
      id: uuidv4(),
      role: 'assistant',
      content: text,
      timestamp: new Date(),
      modelId: normalizedModelId,
      modelDisplayName: this.labelFor(normalizedModelId),
      ...(image ? { image } : {}),
      tokens,
      uid,
      sessionId
    } as any;
  }

  private isMediaMessage(msg: any): boolean {
    const modelId = String(msg?.modelId || '').toLowerCase().trim();
    return (
      Boolean(msg?.image || msg?.video || msg?.thumbnail || msg?.videoId) ||
      ['stability', 'stability35l', 'geminiproimage', 'dale', 'dale2legacy', 'veo', 'veo31', 'cogvideox3', 'novareel11'].includes(modelId)
    );
  }

  private buildTextOnlyHistory(messages: Message[], maxItems: number = 5): Message[] {
    const filtered = (messages || []).filter((m) => !this.isMediaMessage(m));
    return filtered.slice(-maxItems);
  }

  sendPixtralMessage(content: string, image?: File): void {
    if (!content.trim()) return;
    this.ensureActiveSession();

    let c = this.currentConversationSubject.value;
    if (!c) {
      this.createNewConversation().subscribe((conv) => {
        this.setCurrentConversation(conv);
        this.sendPixtralMessage(content, image);
      });
      return;
    }

    const userMsg = this.createUserMsg(content);
    c.messages.push(userMsg);
    this.updateConversation(c).subscribe();
    void this.safeAddFirestoreMessage(c.id, userMsg);

    this.isLoadingSubject.next(true);
    const history = this.buildTextOnlyHistory(c.messages, 5);

    const ai: any = this.aiService;
    ai.generatePixtralImageText(content, history, image).subscribe({
      next: (resp: any) => {
        const text = resp?.text || '';
        const img = resp?.image;
        const assistantMsg = this.createAssistantMsg('pixtralimage', text, img);
        c!.messages.push(assistantMsg);
        this.updateConversation(c!).subscribe();
        void this.safeAddFirestoreMessage(c!.id, assistantMsg);
        this.isLoadingSubject.next(false);
      },
      error: () => {
        const assistantMsg = this.createAssistantMsg('pixtralimage', 'Sorry, I encountered an error.');
        c!.messages.push(assistantMsg);
        this.updateConversation(c!).subscribe();
        void this.safeAddFirestoreMessage(c!.id, assistantMsg);
        this.isLoadingSubject.next(false);
      }
    });
  }

  sendCogVideoX3(prompt: string, image?: File, clientRunId?: string): void {
    if (!prompt.trim() && !image) return;
    this.ensureActiveSession();

    let c = this.currentConversationSubject.value;
    if (!c) {
      this.createNewConversation().subscribe(() => this.sendCogVideoX3(prompt, image, clientRunId));
      return;
    }

    c.messages.push(this.createUserMsg(prompt));
    this.updateConversation(c).subscribe();

    const videoMsg: any = this.createAssistantMsg('cogvideox3', '🎬 Generating video with CogVidX from Z.ai…');
    videoMsg.progress = 50;
    videoMsg.thumbnail = null;
    videoMsg.video = null;
    videoMsg.videoId = null;
    videoMsg.status = 'queued';
    videoMsg.inputReferenceUsed = !!image;
    if (clientRunId) videoMsg.clientRunId = clientRunId;

    c.messages.push(videoMsg);
    this.updateConversation(c).subscribe();

    this.isLoadingSubject.next(true);

    const uid = this.ensureUidOrThrow();
    const sessionId = this.ensureSessionIdOrThrow();

    const ai: any = this.aiService;
    ai.cogVideoX3Gen({
      prompt,
      uid,
      sessionId,
      imageFile: image,
      quality: 'quality',
      withAudio: true,
      size: '1920x1080',
      fps: 30,
      requestId: clientRunId
    })
      .subscribe({
        next: (job: VideoJobUpdate) => {
          videoMsg.videoId = job.id;
          videoMsg.status = job.status;
          videoMsg.progress = typeof job.progress === 'number' ? job.progress : videoMsg.progress;

          if (job.thumbnailUrl) videoMsg.thumbnail = job.thumbnailUrl;

          if (job.status === 'completed' && job.videoUrl) {
            videoMsg.video = job.videoUrl;
            videoMsg.content = '🎥 Video ready';
            videoMsg.progress = 100;
          }

          if (job.status === 'failed') {
            videoMsg.content = `❌ Video generation failed${job.error ? `: ${job.error}` : ''}`;
          }

          this.updateConversation(c!).subscribe();
        },
        error: () => {
          videoMsg.content = '❌ Video generation failed';
          videoMsg.status = 'error';
          this.updateConversation(c!).subscribe();
          this.isLoadingSubject.next(false);
        },
        complete: () => this.isLoadingSubject.next(false)
      });
  }

  sendNovaReel11(prompt: string, image?: File, clientRunId?: string): void {
    if (!prompt.trim() && !image) return;
    this.ensureActiveSession();

    let c = this.currentConversationSubject.value;
    if (!c) {
      this.createNewConversation().subscribe(() => this.sendNovaReel11(prompt, image, clientRunId));
      return;
    }

    c.messages.push(this.createUserMsg(prompt));
    this.updateConversation(c).subscribe();

    const videoMsg: any = this.createAssistantMsg('novareel11', '🎬 Generating video with Nova Reel 1.1…');
    videoMsg.progress = 10;
    videoMsg.thumbnail = null;
    videoMsg.video = null;
    videoMsg.videoId = null;
    videoMsg.status = 'queued';
    videoMsg.inputReferenceUsed = !!image;
    if (clientRunId) videoMsg.clientRunId = clientRunId;

    c.messages.push(videoMsg);
    this.updateConversation(c).subscribe();

    this.isLoadingSubject.next(true);

    const uid = this.ensureUidOrThrow();
    const sessionId = this.ensureSessionIdOrThrow();

    const ai: any = this.aiService;
    ai.novaReel11Gen({
      prompt,
      uid,
      sessionId,
      imageFile: image,
      requestId: clientRunId
    }).subscribe({
      next: (job: VideoJobUpdate) => {
        videoMsg.videoId = job.id;
        videoMsg.status = job.status;
        videoMsg.progress = typeof job.progress === 'number' ? job.progress : videoMsg.progress;

        if (job.thumbnailUrl) videoMsg.thumbnail = job.thumbnailUrl;

        if (job.status === 'completed' && job.videoUrl) {
          videoMsg.video = job.videoUrl;
          videoMsg.content = '🎥 Video ready';
          videoMsg.progress = 100;
        }

        if (job.status === 'failed') {
          videoMsg.content = `❌ Video generation failed${job.error ? `: ${job.error}` : ''}`;
        }

        this.updateConversation(c!).subscribe();
      },
      error: (err) => {
        videoMsg.content = `❌ Video generation failed${err?.message ? `: ${err.message}` : ''}`;
        videoMsg.status = 'error';
        this.updateConversation(c!).subscribe();
        this.isLoadingSubject.next(false);
      },
      complete: () => this.isLoadingSubject.next(false)
    });
  }

  sendVeoVideo(prompt: string, clientRunId?: string): void {
    if (!prompt.trim()) return;
    this.ensureActiveSession();

    let c = this.currentConversationSubject.value;
    if (!c) {
      this.createNewConversation().subscribe(() => this.sendVeoVideo(prompt, clientRunId));
      return;
    }

    c.messages.push(this.createUserMsg(prompt));
    this.updateConversation(c).subscribe();

    const videoMsg: any = this.createAssistantMsg('veo', '🎬 Generating video with Veo 3.1…');
    videoMsg.progress = 10;
    videoMsg.thumbnail = null;
    videoMsg.video = null;
    videoMsg.videoId = null;
    videoMsg.status = 'queued';
    if (clientRunId) videoMsg.clientRunId = clientRunId;

    c.messages.push(videoMsg);
    this.updateConversation(c).subscribe();

    this.isLoadingSubject.next(true);

    const uid = this.ensureUidOrThrow();
    const sessionId = this.ensureSessionIdOrThrow();

    const ai: any = this.aiService;
    ai.veoVideoGen({ prompt, uid, sessionId, modelId: 'veo-3.1-generate-001' })
      .subscribe({
        next: (job: VideoJobUpdate) => {
          videoMsg.videoId = job.id;
          videoMsg.status = job.status;
          videoMsg.progress = typeof job.progress === 'number' ? job.progress : videoMsg.progress;

          if (job.thumbnailUrl) videoMsg.thumbnail = job.thumbnailUrl;

          if (job.status === 'completed' && job.videoUrl) {
            videoMsg.video = job.videoUrl;
            videoMsg.content = '🎥 Video ready';
            videoMsg.progress = 100;
          }

          if (job.status === 'failed') {
            videoMsg.content = `❌ Video generation failed${job.error ? `: ${job.error}` : ''}`;
          }

          this.updateConversation(c!).subscribe();
        },
        error: () => {
          videoMsg.content = '❌ Video generation failed';
          videoMsg.status = 'error';
          this.updateConversation(c!).subscribe();
          this.isLoadingSubject.next(false);
        },
        complete: () => this.isLoadingSubject.next(false)
      });
  }

  sendUiVideo(
    prompt: string,
    modelId: 'veo' | 'veo31' | 'cogvideox3',
    opts?: {
      seconds?: number;
      aspectRatio?: '16:9' | '9:16';
      clientRunId?: string;
      imageBase64?: string;
      resolution?: '720p' | '1080p';
    }
  ): void {
    const safePrompt = String(prompt || '').trim();
    const hasImage = !!String(opts?.imageBase64 || '').trim();
    if (!safePrompt && !hasImage) return;
    this.ensureActiveSession();

    let c = this.currentConversationSubject.value;
    if (!c) {
      this.createNewConversation().subscribe(() => this.sendUiVideo(prompt, modelId, opts));
      return;
    }

    const userPromptText = safePrompt || 'Reference image provided.';
    c.messages.push(this.createUserMsg(userPromptText));
    this.updateConversation(c).subscribe();

    const videoMsg: any = this.createAssistantMsg(
      modelId,
      `🎬 Generating video with ${this.labelFor(modelId)}…`
    );
    videoMsg.progress = modelId === 'cogvideox3' ? 50 : 10;
    videoMsg.thumbnail = null;
    videoMsg.video = null;
    videoMsg.videoId = null;
    videoMsg.status = 'queued';
    if (opts?.clientRunId) videoMsg.clientRunId = opts.clientRunId;

    c.messages.push(videoMsg);
    this.updateConversation(c).subscribe();

    this.isLoadingSubject.next(true);

    const uid = this.ensureUidOrThrow();
    const sessionId = this.ensureSessionIdOrThrow();

    const ai: any = this.aiService;
    const job$ =
      modelId === 'cogvideox3'
        ? ai.cogVideoX3Gen({
            prompt: safePrompt,
            uid,
            sessionId,
            quality: 'quality',
            withAudio: true,
            size: '1920x1080',
            fps: 30,
            requestId: opts?.clientRunId
          })
        : ai.veoVideoGen({
            prompt: safePrompt,
            uid,
            sessionId,
            modelId: 'veo-3.1-lite-generate-preview',
            seconds: (opts?.seconds as 4 | 6 | 8 | undefined) ?? 6,
            aspectRatio: opts?.aspectRatio ?? '16:9',
            resolution: opts?.resolution ?? '720p',
            imageBase64: opts?.imageBase64
          });

    job$.subscribe({
      next: (job: VideoJobUpdate) => {
        // Example-aligned handling: status + url + error
        const videoUrl = job.url || job.videoUrl || null;
        videoMsg.videoId = job.id;
        videoMsg.status = job.status;
        videoMsg.progress = typeof job.progress === 'number' ? job.progress : videoMsg.progress;

        if (job.thumbnailUrl) videoMsg.thumbnail = job.thumbnailUrl;

        if (job.status === 'completed' && videoUrl) {
          videoMsg.video = videoUrl;
          videoMsg.content = '🎥 Video ready';
          videoMsg.progress = 100;
        }

        if (job.status === 'failed') {
          videoMsg.content = `❌ Video generation failed${job.error ? `: ${job.error}` : ''}`;
        }

        this.updateConversation(c!).subscribe();
      },
      error: (err) => {
        videoMsg.content = `❌ Video generation failed${err?.message ? `: ${err.message}` : ''}`;
        videoMsg.status = 'error';
        this.updateConversation(c!).subscribe();
        this.isLoadingSubject.next(false);
      },
      complete: () => this.isLoadingSubject.next(false)
    });
  }

  sendNova2Message(content: string, image?: File): void {
    if (!content.trim()) return;
    this.ensureActiveSession();

    let c = this.currentConversationSubject.value;
    if (!c) {
      this.isLoadingSubject.next(true);
      this.createNewConversation().subscribe({
        next: (conv) => {
          this.setCurrentConversation(conv);
          this.sendNova2Message(content, image);
        },
        error: () => this.isLoadingSubject.next(false)
      });
      return;
    }

    this.isLoadingSubject.next(true);
    const userMsg = this.createUserMsg(content);
    c.messages.push(userMsg);
    this.updateConversation(c).subscribe();
    void this.safeAddFirestoreMessage(c.id, userMsg);
    const history = this.buildTextOnlyHistory(c.messages, 5);

    const ai: any = this.aiService;
    ai.generateNova2Text(content, history, image).subscribe({
      next: (resp: any) => {
        const assistantMsg = this.createAssistantMsg('nova2', resp?.text || '', resp?.image);
        c!.messages.push(assistantMsg);
        this.updateConversation(c!).subscribe();
        void this.safeAddFirestoreMessage(c!.id, assistantMsg);
        this.isLoadingSubject.next(false);
      },
      error: () => {
        const assistantMsg = this.createAssistantMsg('nova2', 'Sorry, I encountered an error.');
        c!.messages.push(assistantMsg);
        this.updateConversation(c!).subscribe();
        void this.safeAddFirestoreMessage(c!.id, assistantMsg);
        this.isLoadingSubject.next(false);
      }
    });
  }

  sendNovaProMessage(content: string, image?: File): void {
    if (!content.trim()) return;
    this.ensureActiveSession();

    let c = this.currentConversationSubject.value;
    if (!c) {
      this.isLoadingSubject.next(true);
      this.createNewConversation().subscribe({
        next: (conv) => {
          this.setCurrentConversation(conv);
          this.sendNovaProMessage(content, image);
        },
        error: () => this.isLoadingSubject.next(false)
      });
      return;
    }

    this.isLoadingSubject.next(true);
    const userMsg = this.createUserMsg(content);
    c.messages.push(userMsg);
    this.updateConversation(c).subscribe();
    void this.safeAddFirestoreMessage(c.id, userMsg);
    const history = this.buildTextOnlyHistory(c.messages, 5);

    const ai: any = this.aiService;
    ai.generateNovaProText(content, history, image).subscribe({
      next: (resp: any) => {
        const assistantMsg = this.createAssistantMsg('novapro', resp?.text || '', resp?.image);
        c!.messages.push(assistantMsg);
        this.updateConversation(c!).subscribe();
        void this.safeAddFirestoreMessage(c!.id, assistantMsg);
        this.isLoadingSubject.next(false);
      },
      error: () => {
        const assistantMsg = this.createAssistantMsg('novapro', 'Sorry, I encountered an error.');
        c!.messages.push(assistantMsg);
        this.updateConversation(c!).subscribe();
        void this.safeAddFirestoreMessage(c!.id, assistantMsg);
        this.isLoadingSubject.next(false);
      }
    });
  }

  sendJambaLargeMessage(content: string, length: AnswerLength = 'normal'): Observable<void> {
    return this.toObservable(async () => {
      if (!content.trim()) return;
      this.isLoadingSubject.next(true);
      try {
        this.ensureActiveSession();

        let c = this.currentConversationSubject.value;
        if (!c) c = await firstValueFrom(this.createNewConversation());

        const userMsg = this.createUserMsg(content);
        c.messages.push(userMsg);
        await firstValueFrom(this.updateConversation(c));
        await this.safeAddFirestoreMessage(c.id, userMsg);

        const history = this.buildTextOnlyHistory(c.messages, 5);

        try {
          const text = await firstValueFrom(this.aiService.generateJambaLargeText(content, history, length));
          const assistantMsg = this.createAssistantMsg('jamba', text ?? '');
          c.messages.push(assistantMsg);
          await firstValueFrom(this.updateConversation(c));
          await this.safeAddFirestoreMessage(c.id, assistantMsg);
        } catch {
          const assistantMsg = this.createAssistantMsg('jamba', 'Sorry, I encountered an error.');
          c.messages.push(assistantMsg);
          await firstValueFrom(this.updateConversation(c));
          await this.safeAddFirestoreMessage(c.id, assistantMsg);
        } finally {
          this.isLoadingSubject.next(false);
        }
      } catch (e) {
        this.isLoadingSubject.next(false);
        throw e;
      }
    });
  }

  sendMessage(
    content: string,
    model: string = 'palmx',
    webSearch = false,
    image?: File,
    numImages: number = 1,
    length?: AnswerLength
  ): Observable<void> {
    return this.toObservable(async () => {
      const requestedModel = this.normalizeRequestedModelId(model);

      if (requestedModel === 'nova2') { this.sendNova2Message(content, image); return; }
      if (requestedModel === 'novapro') { this.sendNovaProMessage(content, image); return; }
      if (!content.trim() && !image) return;

      this.isLoadingSubject.next(true);
      try {
      this.ensureActiveSession();

      let c = this.currentConversationSubject.value;
      if (!c) c = await firstValueFrom(this.createNewConversation());

      const userMsg = this.createUserMsg(content);
      c.messages.push(userMsg);
      await firstValueFrom(this.updateConversation(c));
      await this.safeAddFirestoreMessage(c.id, userMsg);

      const history = this.buildTextOnlyHistory(c.messages, 5);

      const ai: any = this.aiService;
      type ImageResponse = { image?: string; revisedPrompt?: string; text?: string; imageRef?: ImageRef | null };
      type TextResponse = { text?: string };
      const retryEligibleModels = this.aiService.getRetryModelOrder();
      const maxRetries = 3;
      const retryEnabled = !['dale', 'dale2legacy', 'stability', 'stability35l', 'geminiproimage'].includes(
        requestedModel
      );
      const modelsToTry = retryEnabled
        ? this.aiService.buildChatModelsToTry(requestedModel, maxRetries)
        : [requestedModel];

      const runModelAttempt = async (
        activeModel: string
      ): Promise<{ handled: true } | { handled: false; text: string; image: string | null; imageRef?: ImageRef | null }> => {
        const useRealtimeGemini = activeModel === 'gemini' || activeModel === 'gemini3flash';
        if (useRealtimeGemini) {
          try {
            await this.requestGeminiReply(c!.id);
            return { handled: true };
          } catch (err) {
            console.warn('[ChatFireService] Gemini callable failed, falling back.', err);
          }
        }

        let responseText = '';
        let responseImage: string | null = null;

        switch (activeModel) {
          case 'dale': {
            const out = await firstValueFrom(
              ai.generateDalE3Image(content, { quality: 'hd', size: '1024x1024' }) as Observable<ImageResponse>
            );
            responseImage = out?.image || null;
            responseText = out?.revisedPrompt || '(DALL·E 3 image generated)';
            break;
          }

          case 'dale2legacy': {
            if (!image) { responseText = 'Please attach a reference image for DALL-E 2 Legacy.'; break; }
            const out = await firstValueFrom(
              ai.generateDalE2LegacyImage(content, image, { size: '1024x1024' }) as Observable<ImageResponse>
            );
            responseImage = out?.image || null;
            responseText = '(DALL·E 2 legacy image generated)';
            break;
          }

          case 'geminiproimage': {
            const out = await firstValueFrom(
              ai.generateGeminiProImage(content, history, image, { model: 'gemini-3-pro-image-preview' }) as Observable<ImageResponse>
            );
            responseImage = out?.image || null;
            responseText = (out?.text || '').trim() || '(Gemini Pro image generated)';
            const imageRef = out?.imageRef || null;
            if (imageRef) {
              return { handled: false, text: responseText, image: responseImage, imageRef };
            }
            break;
          }

          case 'stability': {
            const total = Math.min(Math.max(numImages || 1, 1), 4);
            const resp = await firstValueFrom(ai.generateStabilityImage({ prompt: content, numberOfImages: total }));
            const images: string[] = Array.isArray((resp as { images?: string[] })?.images) ? (resp as { images?: string[] }).images : [];

            if (images.length === 0) {
              responseText = 'No image generated.';
              const assistantMsg = this.createAssistantMsg('stability', responseText, null);
              c!.messages.push(assistantMsg);
              await this.safeAddFirestoreMessage(c!.id, assistantMsg);
            } else {
              for (const [idx, img] of images.entries()) {
                const caption = images.length > 1 ? `(Stability image ${idx + 1}/${images.length})` : '(Stability image generated)';
                const assistantMsg = this.createAssistantMsg('stability', caption, img);
                c!.messages.push(assistantMsg);
                await this.safeAddFirestoreMessage(c!.id, assistantMsg);
              }
            }

            await firstValueFrom(this.updateConversation(c!));
            return { handled: true };
          }

          case 'stability35l': {
            const out = await firstValueFrom(
              ai.generateStabilitySd35Large({
                prompt: content,
                referenceImage: image || null,
                aspectRatio: '1:1',
                outputFormat: 'png',
                strength: 0.75
              }) as Observable<{ image: string; images: string[] }>
            );
            const img = out?.image || (Array.isArray(out?.images) ? out.images[0] : null);
            responseImage = img || null;
            responseText = '(Stability SD 3.5 Large image generated)';
            break;
          }

          case 'llama':
            responseText = await firstValueFrom(ai.generateLlamaText(content, history));
            break;

          case 'haiku45':
            responseText = await firstValueFrom(ai.generateHaiku45Text(content, history));
            break;

          case 'opus46':
          case 'opus6':
            responseText = await firstValueFrom(ai.generateOpyDopy6(content, history));
            break;

          case 'opus47':
            responseText = await firstValueFrom(ai.generateOpus47Text(content, history));
            break;

          case 'kimik25':
            responseText = await firstValueFrom(ai.generateKimiK25Text(content, history));
            break;
          case 'qwen':
            responseText = await firstValueFrom(ai.generateQwenCoderText(content, history));
            break;
          case 'glm5':
            responseText = await firstValueFrom(ai.getGLM5Chat(content, history));
            break;

          case 'opus37':
            responseText = await firstValueFrom(ai.generateOpus37Text(content, history));
            break;

          case 'opuslegacy':
            responseText = await firstValueFrom(ai.generateOpusLegacyText(content, history));
            break;

          case 'sonnet45':
            responseText = await firstValueFrom(ai.generateSonnet45Text(content, history));
            break;

          case 'claude4': {
            if (image) {
              const out = await firstValueFrom(
                ai.generateClaude4TextImage(content, history, image) as Observable<{ text?: string; image?: string }>
              );
              responseText = String(out?.text ?? '');
              responseImage = out?.image ?? null;
            } else {
              responseText = await firstValueFrom(ai.generateClaude4Text(content, history));
            }
            break;
          }

          case 'palmx':
            responseText = await firstValueFrom(ai.generatePalmXText(content, history));
            break;

          case 'jamba':
            responseText = await firstValueFrom(ai.generateJambaText(content, history, length));
            break;

          case 'maverick':
            responseText = await firstValueFrom(ai.generateMaverickText(content, history));
            break;

          case 'scout':
            responseText = await firstValueFrom(ai.generateScoutText(content, history, length));
            break;

          case 'scoutInstruct':
            responseText = await firstValueFrom(ai.generateBedrockScoutInstructText(content, history, length));
            break;

          case 'commandRInstruct':
            responseText = await firstValueFrom(ai.generateBedrockCommandRInstructText(content, history, length));
            break;

          case 'llama31Instruct':
            responseText = await firstValueFrom(ai.generateBedrockMetaLlamaInstructText(content, history, length));
            break;

          case 'jambaInstruct':
            responseText = await firstValueFrom(ai.generateBedrockJambaInstructText(content, history, length));
            break;

          case 'novalite':
            responseText = await firstValueFrom(ai.generateBedrockNovaLiteText(content, history, length));
            break;

          case 'chatgpt54':
            if (typeof ai.generateChatGPT54ReasonedText === 'function') {
              if (image) {
                responseText = await firstValueFrom(ai.generateChatGPT54Text(content, history, image));
              } else {
                responseText = await firstValueFrom(ai.generateChatGPT54ReasonedText(content, history));
              }
            } else {
              responseText = await firstValueFrom(ai.generateChatGPT54Text(content, history, image));
            }
            break;
          case 'minichatgpt54':
            responseText = await firstValueFrom(ai.generateMiniChatGPT54Text(content, history));
            break;
          case 'cohere':
            responseText = await firstValueFrom(ai.generateCohereText(content, history));
            break;

          case 'commandLight':
            responseText = await firstValueFrom(ai.generateCommandLightText(content, history));
            break;

          case 'mistrallarge':
            responseText = await firstValueFrom(ai.generateMistralLargeText(content, history));
            break;

          case 'codestralto':
            responseText = await firstValueFrom(ai.generateCodestraltoText(content, history));
            break;

          case 'deepseek':
            responseText = await firstValueFrom(ai.generateDeepSeekText(content, history));
            break;

          case 'deepv3':
            responseText = await firstValueFrom(ai.generateDeepSeekV3Text(content, history));
            break;

          case 'gemini':
          case 'gemini3flash':
            responseText = await firstValueFrom(ai.generateGeminiText(content, history, image, 'MINIMAL', 'high'));
            break;

          case 'zai':
            responseText = await firstValueFrom(ai.generateZaiChatText(content, history));
            break;

          case 'pixtralimage': {
            const out = (await firstValueFrom(ai.generatePixtralImageText(content, history, image))) as
              | { text?: string; image?: string | null }
              | null
              | undefined;
            responseText = String(out?.text ?? '');
            responseImage = out?.image ?? null;
            break;
          }

          case 'pegasus':
            if (!image) {
              responseText = 'Please attach a reference image for Pegasus.';
              break;
            }
            responseText = await firstValueFrom(ai.generatePegasusText(content, history, image));
            break;

          default:
            if (typeof ai.generateChatGPT54ReasonedText === 'function') {
              if (image) {
                responseText = await firstValueFrom(ai.generateChatGPT54Text(content, history, image));
              } else {
                responseText = await firstValueFrom(ai.generateChatGPT54ReasonedText(content, history));
              }
            } else {
              responseText = await firstValueFrom(ai.generateChatGPT54Text(content, history, image));
            }
            break;
        }

        return { handled: false, text: responseText, image: responseImage, imageRef: null };
      };

      let lastModelTried = requestedModel;

      try {
        for (const activeModel of modelsToTry) {
          lastModelTried = activeModel;
          try {
            const result = await runModelAttempt(activeModel);
            const unhandled = result.handled
              ? null
              : (result as { handled: false; text: string; image: string | null; imageRef?: ImageRef | null });
            if (unhandled) {
              const text = unhandled.text ?? '';
              const image = unhandled.image ?? null;
              const assistantMsg: any = this.createAssistantMsg(activeModel, text, image);
              if (unhandled.imageRef) assistantMsg.imageRef = unhandled.imageRef;
              c.messages.push(assistantMsg);
              await firstValueFrom(this.updateConversation(c));
              await this.safeAddFirestoreMessage(c.id, assistantMsg);
              return;
            }
            return;
          } catch (err) {
            if (retryEligibleModels.includes(activeModel)) {
              this.aiService.markRetryModelFailed(activeModel);
            }
            if (activeModel !== modelsToTry[modelsToTry.length - 1]) {
              console.warn(`[ChatFireService] Model ${activeModel} failed. Retrying with next model.`, err);
              continue;
            }
            throw err;
          }
        }
      } catch {
        const assistantMsg = this.createAssistantMsg(lastModelTried, 'Sorry, I encountered an error.');
        c.messages.push(assistantMsg);
        await firstValueFrom(this.updateConversation(c));
        await this.safeAddFirestoreMessage(c.id, assistantMsg);
      } finally {
        this.isLoadingSubject.next(false);
      }
      } catch (e) {
        this.isLoadingSubject.next(false);
        throw e;
      }
    });
  }

  sendAudioTextMessage(content: string): Observable<void> {
    return this.toObservable(async () => {
      if (!content.trim()) return;
      this.isLoadingSubject.next(true);
      try {
        this.ensureActiveSession();

        let c = this.currentConversationSubject.value;
        if (!c) c = await firstValueFrom(this.createNewConversation());

        const userMsg = this.createUserMsg(content);
        c.messages.push(userMsg);
        await firstValueFrom(this.updateConversation(c));
        await this.safeAddFirestoreMessage(c.id, userMsg);

        const history = this.buildTextOnlyHistory(c.messages, 5);

        try {
          const text = String(
            await firstValueFrom((this.aiService as any).audioWithGpt4o({ prompt: content, history, realtime: false }))
          );
          const assistantMsg = this.createAssistantMsg('gpt4o_audio_text', text);
          c.messages.push(assistantMsg);
          await firstValueFrom(this.updateConversation(c));
          await this.safeAddFirestoreMessage(c.id, assistantMsg);
        } catch {
          const assistantMsg = this.createAssistantMsg('gpt4o_audio_text', 'Sorry, I encountered an error.');
          c.messages.push(assistantMsg);
          await firstValueFrom(this.updateConversation(c));
          await this.safeAddFirestoreMessage(c.id, assistantMsg);
        } finally {
          this.isLoadingSubject.next(false);
        }
      } catch (e) {
        this.isLoadingSubject.next(false);
        throw e;
      }
    });
  }

  startRealtimeAudio(voice?: string): Observable<void> {
    return this.toObservable(async () => {
      this.ensureActiveSession();

      if (this.rtSession) {
        try { this.rtSession.close(); } catch {}
        this.rtSession = null;
        this.rtEventsSub?.unsubscribe();
        this.rtEventsSub = null;
        this.realtimeActiveSubject.next(false);
        this.remoteAudioElSubject.next(null);
      }

      let c = this.currentConversationSubject.value;
      if (!c) c = await firstValueFrom(this.createNewConversation());

      c.messages.push(this.createAssistantMsg('gpt4o_audio_realtime', `Voice session started. I'm listening…`));
      await firstValueFrom(this.updateConversation(c));

      let pendingText = '';

      try {
        const session: any = await firstValueFrom(this.aiService.startOpenAiRtcUi({
          voice,
          instructions: 'You are a helpful AI assistant. Keep responses conversational.'
        }));

        this.rtEventsSub?.unsubscribe();
        this.rtEventsSub = session.events$.subscribe({
          next: async (event: any) => {
            const type = String(event?.type || '');
            if (type === 'response.text.delta') {
              const delta = String(event?.delta || event?.text || '');
              if (delta) pendingText += delta;
            }
            if (type === 'response.done' || type === 'response.completed') {
              this.writeLastActiveNow();
              if (!pendingText.trim()) return;
              const msg = this.createAssistantMsg('gpt4o_audio_realtime', pendingText);
              pendingText = '';
              c!.messages.push(msg);
              await firstValueFrom(this.updateConversation(c!));
            }
            if (type === 'error') {
              this.writeLastActiveNow();
              c!.messages.push(this.createAssistantMsg('gpt4o_audio_realtime', 'Voice session error.'));
              await firstValueFrom(this.updateConversation(c!));
            }
          },
          error: async (e) => {
            this.writeLastActiveNow();
            c!.messages.push(this.createAssistantMsg('gpt4o_audio_realtime', 'Voice session error.'));
            await firstValueFrom(this.updateConversation(c!));
            console.error(e);
          }
        });

        this.rtSession = {
          pc: session.pc,
          dc: session.dc,
          localStream: session.localStream,
          remoteAudioEl: session.remoteAudioEl,
          sendText: session.sendText,
          muteMic: session.muteMic,
          startUserTurn: session.startUserTurn,
          endUserTurn: session.endUserTurn,
          close: session.close
        };
        this.realtimeActiveSubject.next(true);
        this.remoteAudioElSubject.next(session.remoteAudioEl);
      } catch {
        c.messages.push(this.createAssistantMsg('gpt4o_audio_realtime', 'Failed to start voice session.'));
        await firstValueFrom(this.updateConversation(c));
      }
    });
  }

  beginRealtimeAudioTurn(): Observable<void> {
    return this.toObservable(async () => {
      this.ensureActiveSession();
      if (!this.rtSession) {
        await firstValueFrom(this.startRealtimeAudio());
      }
      if (!this.rtSession) return;
      this.writeLastActiveNow();
      this.rtSession.startUserTurn?.();
    });
  }

  endRealtimeAudioTurn(): Observable<void> {
    return this.toObservable(async () => {
      if (!this.rtSession) return;
      this.writeLastActiveNow();
      this.rtSession.endUserTurn?.();
    });
  }

  sendRealtimeText(text: string): void {
    if (!this.rtSession) return;
    this.writeLastActiveNow();
    this.rtSession.sendText(text);
  }

  muteRealtimeMic(muted: boolean): void {
    if (!this.rtSession) return;
    this.writeLastActiveNow();
    this.rtSession.muteMic(muted);
  }

  stopRealtimeAudio(): Observable<void> {
    return this.toObservable(async () => {
      if (!this.rtSession) return;
      this.writeLastActiveNow();

      try { this.rtSession.close(); } catch {}
      this.rtSession = null;
      this.rtEventsSub?.unsubscribe();
      this.rtEventsSub = null;
      this.realtimeActiveSubject.next(false);
      this.remoteAudioElSubject.next(null);

      const c = this.currentConversationSubject.value;
      if (c) {
        c.messages.push(this.createAssistantMsg('gpt4o_audio_realtime', 'Voice session ended.'));
        await firstValueFrom(this.updateConversation(c));
      }
    });
  }

  deleteConversation(id: string): Observable<void> {
    return this.toObservable(async () => {
      this.ensureActiveSession();

      const uid = this.ensureUidOrThrow();
      const rootPath = this.namespaceRootPath(uid, this.historyNamespaceSubject.value);
      const remaining = this.conversationsSubject.value.filter((v) => v.id !== id);
      this.conversationsSubject.next(remaining);

      if (this.currentConversationSubject.value?.id === id) {
        this.setCurrentConversation(null);
      }

      try {
        await remove(ref(database, `${rootPath}/${id}`));
      } catch {}
    });
  }

  clearAllConversations(): Observable<void> {
    return this.toObservable(async () => {
      this.ensureActiveSession();
      const uid = this.ensureUidOrThrow();
      const rootPath = this.namespaceRootPath(uid, this.historyNamespaceSubject.value);
      this.conversationsSubject.next([]);
      this.setCurrentConversation(null);
      try { await remove(ref(database, rootPath)); } catch {}
    });
  }

  deleteOldConversations(daysOld: number = 30): Observable<void> {
    return this.toObservable(async () => {
      const rootPath = this.conversationsRootPath();
      if (!rootPath) return;

      try {
        const snapshot = await get(child(ref(database), rootPath));
        if (!snapshot.exists()) return;

        const data = snapshot.val();
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - daysOld);
        const cutoffMs = cutoffDate.getTime();

        const deleteOps: Array<ReturnType<typeof remove>> = [];

        for (const key in data) {
          if (Object.prototype.hasOwnProperty.call(data, key)) {
            const conv = data[key] as Conversation & { createdAt?: any; updatedAt?: any };

            const updatedAt = conv.updatedAt
              ? new Date(conv.updatedAt).getTime()
              : conv.createdAt
                ? new Date(conv.createdAt).getTime()
                : 0;

            if (updatedAt > 0 && updatedAt < cutoffMs) {
              deleteOps.push(remove(ref(database, `${rootPath}/${key}`)));
            }
          }
        }

        await firstValueFrom(forkJoin(deleteOps.map((op) => from(op))));
        await firstValueFrom(this.loadConversations());
      } catch (err) {
        console.error('[ChatFireService] deleteOldConversations error', err);
      }
    });
  }

  private setupConversationsListener(): void {
    if (this.isSessionExpired()) {
      this.startNewSession();
      return;
    }

    if (this.conversationsListenerUnsubscribe) {
      this.conversationsListenerUnsubscribe();
      this.conversationsListenerUnsubscribe = null;
      this.conversationsRef = null;
    }

    const rootPath = this.conversationsRootPath();
    const sessionId = this.currentSessionId;

    if (!rootPath || !sessionId) {
      this.conversationsSubject.next([]);
      this.setCurrentConversation(null);
      return;
    }

    try {
      this.conversationsRef = ref(database, rootPath);

      this.conversationsListenerUnsubscribe = onValue(
        this.conversationsRef,
        (snapshot) => {
          const conversations: Conversation[] = [];

          if (snapshot.exists()) {
            const data = snapshot.val();

            const keys = Object.keys(data)
              .map((key) => ({ key, raw: data[key] }))
              .filter(({ raw }) => raw?.sessionId === sessionId);

            const sortedKeys = keys
              .map(({ key, raw }) => ({ key, updatedAt: new Date(raw.updatedAt) }))
              .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
              .slice(0, 5);

            for (const { key } of sortedKeys) {
              const c = data[key] as any;
              c.createdAt = new Date(c.createdAt as any);
              c.updatedAt = new Date(c.updatedAt as any);

              const msgs = Array.isArray(c.messages) ? c.messages : [];
              c.messages = msgs
                .map((m: any) => ({ ...m, timestamp: m?.timestamp ? new Date(m.timestamp) : new Date() }))
                .filter((m: any) => (m?.sessionId ? m.sessionId === sessionId : true));

              conversations.push(c);
            }
          }

          this.conversationsSubject.next(conversations);

          const current = this.currentConversationSubject.value;
          if (!current || !conversations.some((cv) => cv.id === current.id)) {
            this.setCurrentConversation(conversations[0] ?? null);
          }
        },
        (err) => {
          console.error('[ChatFireService] conversations listener error', err);
          this.conversationsSubject.next([]);
          this.setCurrentConversation(null);
        }
      );
    } catch (err) {
      console.error('[ChatFireService] setupConversationsListener error', err);
      this.conversationsSubject.next([]);
      this.setCurrentConversation(null);
    }
  }

  private setupAllConversationsByUidListener(): void {
    const uid = this.currentUid;
    if (!uid) {
      this.allConversationsByUidSubject.next([]);
      return;
    }

    if (this.allConversationsListenerUnsubscribe) {
      this.allConversationsListenerUnsubscribe();
      this.allConversationsListenerUnsubscribe = null;
      this.allConversationsRef = null;
    }

    try {
      const rootPath = this.namespaceRootPath(uid, this.historyNamespaceSubject.value);
      this.allConversationsRef = ref(database, rootPath);

      this.allConversationsListenerUnsubscribe = onValue(
        this.allConversationsRef,
        (snapshot) => {
          const conversations: Conversation[] = [];

          if (snapshot.exists()) {
            const data = snapshot.val();
            const keys = Object.keys(data);

            const sortedKeys = keys
              .map((key) => ({ key, raw: data[key] }))
              .map(({ key, raw }) => ({ key, updatedAt: new Date(raw.updatedAt || raw.createdAt || Date.now()) }))
              .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());

            for (const { key } of sortedKeys) {
              const raw = data[key];
              const c: Conversation = {
                id: raw.id || key,
                title: raw.title || 'Conversation',
                messages: (raw.messages || []).map((m: any) => ({
                  ...m,
                  timestamp: m?.timestamp ? new Date(m.timestamp) : new Date()
                })),
                createdAt: raw.createdAt ? new Date(raw.createdAt) : new Date(),
                updatedAt: raw.updatedAt ? new Date(raw.updatedAt) : new Date(),
                uid: raw.uid,
                sessionId: raw.sessionId,
                topic: raw.topic || this.extractTopicFromMessages(raw.messages || [])
              };
              conversations.push(c);
            }
          }

          this.allConversationsByUidSubject.next(conversations);
        },
        (err) => {
          console.error('[ChatFireService] all conversations by uid listener error', err);
          this.allConversationsByUidSubject.next([]);
        }
      );
    } catch (err) {
      console.error('[ChatFireService] setupAllConversationsByUidListener error', err);
      this.allConversationsByUidSubject.next([]);
    }
  }

  private loadConversations(): Observable<void> {
    return this.toObservable(async () => {
      this.setupConversationsListener();
    });
  }

  /**
   * Filters conversations to a recent window by UID timeline.
   * A conversation is kept if its updated/created timestamp is in range,
   * or if at least one message timestamp is in range.
   */
  filterConversationsForPastDays(
    conversations: Conversation[],
    days: number = 30,
    nowDate: Date = new Date()
  ): Conversation[] {
    const safeDays = Math.max(1, Number(days) || 30);
    const cutoffMs = nowDate.getTime() - safeDays * 24 * 60 * 60 * 1000;

    return (conversations || []).filter((conv) => {
      const updatedAt = this.parseDateMaybe((conv as any)?.updatedAt)?.getTime() || 0;
      const createdAt = this.parseDateMaybe((conv as any)?.createdAt)?.getTime() || 0;
      if (updatedAt >= cutoffMs || createdAt >= cutoffMs) {
        return true;
      }

      const hasRecentMessage = (conv.messages || []).some((msg: any) => {
        const ts = this.parseDateMaybe(msg?.timestamp)?.getTime() || 0;
        return ts >= cutoffMs;
      });
      return hasRecentMessage;
    });
  }

  private parseDateMaybe(value: any): Date | null {
    if (!value) return null;
    const parsed = value instanceof Date ? value : new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
}